import { useState } from 'react';
import axios from 'axios';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Label } from '../ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import { toast } from 'sonner';
import { Loader2, Calendar } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const solutionOptions = [
  { value: 'HR Management', label: 'HR Management' },
  { value: 'Project Management', label: 'Project Management' },
  { value: 'Sales Management', label: 'Sales Management' },
  { value: 'Marketing Management', label: 'Marketing Management' },
  { value: 'Finance Management', label: 'Finance Management' },
  { value: 'Custom IT Solutions', label: 'Custom IT Solutions' },
  { value: 'Multiple Solutions', label: 'Multiple Solutions' },
];

export const DemoPopup = ({ 
  trigger, 
  title = 'Book a Demo',
  formType = 'Demo Request',
  defaultSolution = ''
}) => {
  const [open, setOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    company: '',
    email: '',
    phone: '',
    interested_in: defaultSolution,
    company_size: '',
    message: '',
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSelectChange = (name, value) => {
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const payload = {
        ...formData,
        form_type: formType,
      };

      const response = await axios.post(`${API}/contact`, payload);
      
      if (response.data.success) {
        toast.success('Demo request submitted! Our team will contact you shortly.');
        setFormData({
          name: '',
          company: '',
          email: '',
          phone: '',
          interested_in: defaultSolution,
          company_size: '',
          message: '',
        });
        setOpen(false);
      }
    } catch (error) {
      console.error('Form submission error:', error);
      toast.error('Something went wrong. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        {trigger || (
          <Button
            className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-full px-8 py-6 font-medium glow-primary glow-primary-hover btn-shine"
            data-testid="demo-popup-trigger"
          >
            <Calendar className="w-4 h-4 mr-2" />
            Book a Demo
          </Button>
        )}
      </DialogTrigger>
      <DialogContent className="bg-card border-border max-w-lg max-h-[90vh] overflow-y-auto" data-testid="demo-popup-dialog">
        <DialogHeader>
          <DialogTitle className="text-2xl font-heading text-white">{title}</DialogTitle>
        </DialogHeader>
        
        <form onSubmit={handleSubmit} className="space-y-4 mt-4" data-testid="demo-popup-form">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="popup-name" className="text-white text-sm">Name *</Label>
              <Input
                id="popup-name"
                name="name"
                type="text"
                required
                value={formData.name}
                onChange={handleChange}
                placeholder="Your name"
                className="bg-secondary/50 border-border focus:border-primary h-10"
                data-testid="popup-input-name"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="popup-company" className="text-white text-sm">Company *</Label>
              <Input
                id="popup-company"
                name="company"
                type="text"
                required
                value={formData.company}
                onChange={handleChange}
                placeholder="Your company"
                className="bg-secondary/50 border-border focus:border-primary h-10"
                data-testid="popup-input-company"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="popup-email" className="text-white text-sm">Email *</Label>
              <Input
                id="popup-email"
                name="email"
                type="email"
                required
                value={formData.email}
                onChange={handleChange}
                placeholder="your@email.com"
                className="bg-secondary/50 border-border focus:border-primary h-10"
                data-testid="popup-input-email"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="popup-phone" className="text-white text-sm">Phone *</Label>
              <Input
                id="popup-phone"
                name="phone"
                type="tel"
                required
                value={formData.phone}
                onChange={handleChange}
                placeholder="+91-78146-51194"
                className="bg-secondary/50 border-border focus:border-primary h-10"
                data-testid="popup-input-phone"
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="popup-interested-in" className="text-white text-sm">Interested In *</Label>
            <Select
              value={formData.interested_in}
              onValueChange={(value) => handleSelectChange('interested_in', value)}
            >
              <SelectTrigger className="bg-secondary/50 border-border h-10" data-testid="popup-select-interested-in">
                <SelectValue placeholder="Select a solution..." />
              </SelectTrigger>
              <SelectContent className="bg-secondary border-border">
                {solutionOptions.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="popup-company-size" className="text-white text-sm">Company Size</Label>
            <Select
              value={formData.company_size}
              onValueChange={(value) => handleSelectChange('company_size', value)}
            >
              <SelectTrigger className="bg-secondary/50 border-border h-10" data-testid="popup-select-company-size">
                <SelectValue placeholder="Select company size..." />
              </SelectTrigger>
              <SelectContent className="bg-secondary border-border">
                <SelectItem value="1-10 employees">1-10 employees</SelectItem>
                <SelectItem value="11-50 employees">11-50 employees</SelectItem>
                <SelectItem value="51-200 employees">51-200 employees</SelectItem>
                <SelectItem value="201-500 employees">201-500 employees</SelectItem>
                <SelectItem value="500+ employees">500+ employees</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="popup-message" className="text-white text-sm">Message</Label>
            <Textarea
              id="popup-message"
              name="message"
              value={formData.message}
              onChange={handleChange}
              placeholder="Tell us about your needs..."
              className="bg-secondary/50 border-border focus:border-primary min-h-[80px]"
              data-testid="popup-input-message"
            />
          </div>

          <Button
            type="submit"
            disabled={isSubmitting}
            className="w-full bg-primary text-primary-foreground hover:bg-primary/90 rounded-full py-5 font-medium glow-primary"
            data-testid="popup-submit-btn"
          >
            {isSubmitting ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Submitting...
              </>
            ) : (
              'Request Demo'
            )}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default DemoPopup;
